import React, { useEffect, useContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme, Modal, Button } from 'antd';
import {
  ThunderboltOutlined, MailOutlined, SettingOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Lists from './pages/Lists';
import Subscribers from './pages/Subscribers';
import Campaigns from './pages/Campaigns';
import CreateCampaign from './pages/CreateCampaign';
import Automations from './pages/Automations';
import CreateAutomation from './pages/CreateAutomation';
import Forms from './pages/Forms';
import CreateForm from './pages/CreateForm';
import PublicForm from './pages/PublicForm';
import FormConfirmed from './pages/FormConfirmed';
import Reports from './pages/Reports';
import Suppression from './pages/Suppression';
import Templates from './pages/Templates';
import Settings from './pages/Settings';
import Unsubscribe from './pages/Unsubscribe';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Onboarding from './pages/Onboarding';
import Billing from './pages/Billing';
import DynamicLists from './pages/DynamicLists';
import Deliverability from './pages/Deliverability';
import AccountActivity from './pages/AccountActivity';
import Support from './pages/Support';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrgs from './pages/admin/AdminOrgs';
import AdminOrgDetail from './pages/admin/AdminOrgDetail';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCampaigns from './pages/admin/AdminCampaigns';
import AdminEmailLogs from './pages/admin/AdminEmailLogs';
import AdminBounceReport from './pages/admin/AdminBounceReport';
import AdminSuppression from './pages/admin/AdminSuppression';
import AdminSystem from './pages/admin/AdminSystem';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminBilling from './pages/admin/AdminBilling';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminEngagement from './pages/admin/AdminEngagement';
import AdminSupport from './pages/admin/AdminSupport';
import PrivateRoute from './components/PrivateRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import AppLayout from './components/AppLayout';
import RobotsManager from './components/RobotsManager';

const PUBLIC_PATHS = ['/login', '/register', '/verify-email', '/unsubscribe', '/reset-password', '/onboarding', '/f/', '/form-confirmed'];

// Region-aware Pro price (₹999 for India, $9.99 international) — matches Billing page logic
const _tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; } })();
const UPGRADE_PRICE = (_tz === 'Asia/Kolkata' || _tz === 'Asia/Calcutta') ? '₹999' : '$9.99';

const AxiosInterceptor = () => {
  const navigate = useNavigate();
  const [upgradeModal,      setUpgradeModal]      = useState(false);
  const [upgradeCopy,       setUpgradeCopy]       = useState({
    title: 'Free plan limit reached',
    body:  "You've used your 1 free campaign this month. Upgrade to Pro for unlimited campaigns, email automations, signup forms, dynamic segments, and timezone scheduling.",
  });
  const [upgrading,         setUpgrading]          = useState(false);
  const [emailModal,        setEmailModal]         = useState(false);
  const [sesModal,          setSesModal]           = useState(false);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await axios.post('/api/billing/checkout', {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      window.location.href = res.data.url;
    } catch { setUpgrading(false); }
  };

  useEffect(() => {
    // Attach Bearer token to every outgoing request automatically
    const reqId = axios.interceptors.request.use(config => {
      const token = localStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    const id = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
        }
        if (err.response?.status === 403) {
          const d = err.response.data || {};
          if (d.upgrade) {
            setUpgradeCopy(d.pro_feature
              ? { title: 'Upgrade to Pro', body: d.message || 'This feature is available on the Pro plan.' }
              : { title: 'Free plan limit reached', body: "You've used your 1 free campaign this month. Upgrade to Pro for unlimited campaigns, email automations, signup forms, dynamic segments, and timezone scheduling." });
            setUpgradeModal(true);
          }
          if (d.email_unverified)  setEmailModal(true);
          if (d.ses_not_connected) setSesModal(true);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(id);
    };
  }, [navigate]);

  const iconBox = (bg, icon) => (
    <div style={{
      width: 64, height: 64, borderRadius: '50%', background: bg,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
    }}>
      {React.cloneElement(icon, { style: { fontSize: 28, color: '#fff' } })}
    </div>
  );

  return (
    <>
      {/* ── Free plan limit modal ── */}
      <Modal open={upgradeModal} onCancel={() => setUpgradeModal(false)} footer={null} centered width={440}>
        <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
          {iconBox('linear-gradient(135deg, #f59e0b, #ef4444)', <ThunderboltOutlined />)}
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>{upgradeCopy.title}</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>
            {upgradeCopy.body}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button
              type="primary" size="large" icon={<ThunderboltOutlined />}
              loading={upgrading} onClick={handleUpgrade}
              style={{ borderRadius: 10, fontWeight: 600, height: 46 }}
            >
              Upgrade to Pro — {UPGRADE_PRICE}/month
            </Button>
            <Button
              size="large"
              onClick={() => { setUpgradeModal(false); navigate('/billing'); }}
              style={{ borderRadius: 10, height: 44 }}
            >
              View Plan Details
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Email not verified modal ── */}
      <Modal open={emailModal} onCancel={() => setEmailModal(false)} footer={null} centered width={420}>
        <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
          {iconBox('linear-gradient(135deg, #0a40d9, #2e6bf5)', <MailOutlined />)}
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Verify your email first</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>
            Please verify your email address before sending campaigns. Check your inbox
            for the verification link we sent when you signed up.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button
              type="primary" size="large" icon={<MailOutlined />}
              onClick={() => { setEmailModal(false); navigate('/settings'); }}
              style={{ borderRadius: 10, fontWeight: 600, height: 46 }}
            >
              Go to Account Settings
            </Button>
            <Button
              size="large"
              onClick={() => setEmailModal(false)}
              style={{ borderRadius: 10, height: 44 }}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── SES not connected modal ── */}
      <Modal open={sesModal} onCancel={() => setSesModal(false)} footer={null} centered width={420}>
        <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
          {iconBox('linear-gradient(135deg, #f59e0b, #f97316)', <CloudOutlined />)}
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Connect AWS SES first</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>
            You need to connect and verify your AWS SES account before you can send
            campaigns. Head to Settings to add your credentials.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button
              type="primary" size="large" icon={<SettingOutlined />}
              onClick={() => { setSesModal(false); navigate('/settings#ses'); }}
              style={{ borderRadius: 10, fontWeight: 600, height: 46 }}
            >
              Go to Settings → AWS SES
            </Button>
            <Button
              size="large"
              onClick={() => setSesModal(false)}
              style={{ borderRadius: 10, height: 44 }}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.some(p => location.pathname.startsWith(p));
  const isAuthenticated = !!localStorage.getItem('token');
  if (isPublic || !isAuthenticated) return <>{children}</>;
  return <AppLayout>{children}</AppLayout>;
};

const ThemedApp = () => {
  const { theme } = useContext(ThemeContext);

  const algorithm = theme.mode === 'dark'
    ? [antdTheme.darkAlgorithm]
    : [antdTheme.defaultAlgorithm];

  const antConfig = {
    algorithm,
    token: {
      colorPrimary:  theme.colorPrimary || '#0a40d9',
      colorSuccess:  '#059669',
      colorWarning:  '#d97706',
      colorError:    '#dc2626',
      borderRadius:  theme.borderRadius || 10,
      fontFamily:    "'Inter', system-ui, -apple-system, sans-serif",
      colorBgBase:   '#ffffff',
      colorTextBase: '#1e1b4b',
      lineHeight:    1.45,
    },
  };

  return (
    <ConfigProvider theme={antConfig}>
      <Router>
        <RobotsManager />
        <AxiosInterceptor />
        <LayoutWrapper>
          <Routes>
            <Route path="/login"          element={<Login />} />
            <Route path="/register"       element={<Register />} />
            <Route path="/f/:slug"        element={<PublicForm />} />
            <Route path="/form-confirmed" element={<FormConfirmed />} />
            <Route path="/verify-email"   element={<VerifyEmail />} />
            <Route path="/onboarding"     element={<PrivateRoute><Onboarding /></PrivateRoute>} />
            <Route path="/unsubscribe"    element={<Unsubscribe />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/"                     element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/lists"                element={<PrivateRoute><Lists /></PrivateRoute>} />
            <Route path="/subscribers"          element={<PrivateRoute><Subscribers /></PrivateRoute>} />
            <Route path="/campaigns"            element={<PrivateRoute><Campaigns /></PrivateRoute>} />
            <Route path="/campaigns/create"     element={<PrivateRoute><CreateCampaign /></PrivateRoute>} />
            <Route path="/campaigns/:id/edit"   element={<PrivateRoute><CreateCampaign /></PrivateRoute>} />
            <Route path="/automations"            element={<PrivateRoute><Automations /></PrivateRoute>} />
            <Route path="/automations/create"     element={<PrivateRoute><CreateAutomation /></PrivateRoute>} />
            <Route path="/automations/:id/edit"   element={<PrivateRoute><CreateAutomation /></PrivateRoute>} />
            <Route path="/forms"              element={<PrivateRoute><Forms /></PrivateRoute>} />
            <Route path="/forms/create"       element={<PrivateRoute><CreateForm /></PrivateRoute>} />
            <Route path="/forms/:id/edit"     element={<PrivateRoute><CreateForm /></PrivateRoute>} />
            <Route path="/campaigns/:id/report" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/templates"            element={<PrivateRoute><Templates /></PrivateRoute>} />
            <Route path="/suppression"          element={<PrivateRoute><Suppression /></PrivateRoute>} />
            <Route path="/settings"             element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/billing"              element={<PrivateRoute><Billing /></PrivateRoute>} />
            <Route path="/dynamic-lists"        element={<PrivateRoute><DynamicLists /></PrivateRoute>} />
            <Route path="/deliverability"       element={<PrivateRoute><Deliverability /></PrivateRoute>} />
            <Route path="/activity"             element={<PrivateRoute><AccountActivity /></PrivateRoute>} />
            <Route path="/support"              element={<PrivateRoute><Support /></PrivateRoute>} />

            {/* Super Admin routes */}
            <Route path="/admin"              element={<SuperAdminRoute><AdminDashboard /></SuperAdminRoute>} />
            <Route path="/admin/orgs"         element={<SuperAdminRoute><AdminOrgs /></SuperAdminRoute>} />
            <Route path="/admin/orgs/:id"     element={<SuperAdminRoute><AdminOrgDetail /></SuperAdminRoute>} />
            <Route path="/admin/users"         element={<SuperAdminRoute><AdminUsers /></SuperAdminRoute>} />
            <Route path="/admin/campaigns"    element={<SuperAdminRoute><AdminCampaigns /></SuperAdminRoute>} />
            <Route path="/admin/email-logs"   element={<SuperAdminRoute><AdminEmailLogs /></SuperAdminRoute>} />
            <Route path="/admin/bounces"      element={<SuperAdminRoute><AdminBounceReport /></SuperAdminRoute>} />
            <Route path="/admin/suppression"  element={<SuperAdminRoute><AdminSuppression /></SuperAdminRoute>} />
            <Route path="/admin/system"       element={<SuperAdminRoute><AdminSystem /></SuperAdminRoute>} />
            <Route path="/admin/audit"        element={<SuperAdminRoute><AdminAuditLog /></SuperAdminRoute>} />
            <Route path="/admin/billing"      element={<SuperAdminRoute><AdminBilling /></SuperAdminRoute>} />
            <Route path="/admin/revenue"      element={<SuperAdminRoute><AdminRevenue /></SuperAdminRoute>} />
            <Route path="/admin/engagement"   element={<SuperAdminRoute><AdminEngagement /></SuperAdminRoute>} />
            <Route path="/admin/support"      element={<SuperAdminRoute><AdminSupport /></SuperAdminRoute>} />

            <Route path="*"                     element={<Navigate to="/" />} />
          </Routes>
        </LayoutWrapper>
      </Router>
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
